import { withNamespaces } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { Button, Breadcrumb, BreadcrumbItem } from "reactstrap";

const isNonLinkCrumb = (link) =>
    !link || link === "#" || link === "/";

const labelFor = (value, t, translate) => {
    if (typeof value !== "string") {
        return value;
    }

    return translate ? t(value) : value;
};

/**
 * Shared page header / navigator.
 *
 * Layout: [Back?] [Title?] [actions?] .......... breadcrumb trail (right)
 *
 * @param {string|React.ReactNode} [pageTitle] - Omitted/falsy → no title rendered
 * @param {{ title: string, link?: string }[]} [breadCrumbs]
 * @param {(defaults: object) => object} [backButtonOptions] - Merge/override Back button
 * @param {boolean} [translate=true] - Run t() on string titles / crumb titles
 * @param {React.ReactNode} [children] - Optional actions next to Back/title
 * @param {string} [className]
 */
const Navigator = (props) => {
    const {
        t,
        pageTitle,
        breadCrumbs = [],
        backButtonOptions,
        children,
        className = "",
        translate = true,
    } = props;

    const history = useHistory();

    const backButtonDefaultOptions = {
        enable: false,
        title: "Back",
        icon: <i className="ri-arrow-left-fill"></i>,
        onClick: () => history.goBack(),
    };

    const backButtonResultOptions = {
        ...backButtonDefaultOptions,
        ...(backButtonOptions
            ? backButtonOptions(backButtonDefaultOptions)
            : {}),
    };

    const crumbs = Array.isArray(breadCrumbs) ? breadCrumbs : [];
    const hasTitle = pageTitle !== undefined && pageTitle !== null && pageTitle !== "";
    const hasCrumbs = crumbs.length > 0;
    const showBack = !!backButtonResultOptions?.enable;

    return (
        <div className={`custom-navigator ${className}`.trim()}>
            <div className="navigator-left d-flex align-items-center gap-2">
                {showBack ? (
                    <div className="navigator-actions">
                        <Button
                            className="d-flex gap-1 align-items-center"
                            outline
                            color="secondary"
                            size="sm"
                            onClick={(event) => {
                                backButtonResultOptions?.onClick?.(event);
                            }}
                        >
                            {backButtonResultOptions?.icon ? (
                                <>{backButtonResultOptions.icon}</>
                            ) : null}
                            {labelFor(backButtonResultOptions?.title, t, translate)}
                        </Button>
                    </div>
                ) : null}

                {hasTitle ? (
                    <div className="page-header">
                        <p className="page-header-title mb-0">
                            {labelFor(pageTitle, t, translate)}
                        </p>
                    </div>
                ) : null}

                {children ? (
                    <div className="navigator-extra-actions d-flex gap-1 align-items-center">
                        {children}
                    </div>
                ) : null}
            </div>

            {hasCrumbs ? (
                <div className="navigator-breadcrumbs">
                    <Breadcrumb listClassName="mb-0">
                        {crumbs.map((breadItem, index) => {
                            const isLast = index + 1 === crumbs.length;
                            const title = labelFor(breadItem.title, t, translate);

                            return (
                                <BreadcrumbItem key={index} active={isLast}>
                                    {isLast || isNonLinkCrumb(breadItem.link) ? (
                                        <span>{title}</span>
                                    ) : (
                                        <Link to={breadItem.link}>{title}</Link>
                                    )}
                                </BreadcrumbItem>
                            );
                        })}
                    </Breadcrumb>
                </div>
            ) : null}
        </div>
    );
};

export default withNamespaces()(Navigator);
